// The issues grid's wiring — one bag a page spreads onto `@rdrive/plugin-items-grid`'s IssuesGrid:
//
//   const app = useAppContext();
//   const issuesGrid = useIssuesGrid({ conn: app.conn, filterContext: app.filterIssuesContext, … });
//   <IssuesGrid v-bind="issuesGrid" />
//
// Since 2026-10-12 the grid is a package component (an app's src/components/ holds only pages —
// PLATFORM.md §3.2 rule 9) and holds no SQL, no report and no link opener; what it ran itself
// until then is here, as it was: the data (`useIssuesGridData` — fetchPage, reloadKey, the report's
// where-clause), the report (`useIssuesGridReport` with the root composer's `t` and the GA
// `trackEvent`) and the deep-link opener (`$launchUrl` in production, else a new tab). It is
// project-overview's `useItemsGrid` for this app, and the trio's names mirror project-overview's
// (`useItemsGrid` / `useItemsGridData` / `useItemsGridReport`).
//
// The bag is a `computed` of already-unwrapped values, NOT a `reactive()` object: `reactive()`
// would proxy `filterContext` and auto-unwrap its `filtersLastUpdated`, changing what the table
// deep-watches. Call it in a page's setup (it injects `$launchUrl`, and `useI18n`/`useAnalytics`
// need a component instance).
import { computed, inject, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIssuesGridData } from '@/composables/useIssuesGridData';
import { useIssuesGridReport, ISSUES_REPORT_DISABLED_MODES } from '@/composables/useIssuesGridReport';
import { useAnalytics } from '@/utils/useAnalytics';

/**
 * @param {object} deps
 * @param {object} deps.conn  DuckDB connection (DuckDBService's gated `conn`) — read once, here
 * @param {import('vue').Ref<object>} deps.filterContext  the issues filter context
 * @param {import('vue').Ref<boolean>} deps.dataLoaded  the GLOBAL data-loaded flag (the loader's)
 * @param {import('vue').Ref<object>} deps.settings  the dashboard's settings (apiUrl, projectCode,
 *   projectName — the deep links and the report)
 * @param {import('vue').Ref<string>} deps.buildMode  'production' opens the links through `$launchUrl`
 * @param {import('vue').Ref<string>} deps.logoUrl  the company logo for the report ('' → the default)
 * @param {object} [deps.duckdb]  the DuckDBService — only the report's opt-in plan branch (off) uses it
 * @param {Array<string>|import('vue').Ref<Array<string>>} [deps.conditions]  extra WHERE fragments
 *   (DrawingsPage's Gantt selection)
 * @returns {import('vue').ComputedRef<object>} every key an IssuesGrid prop
 */
export function useIssuesGrid({
    conn,
    filterContext,
    dataLoaded,
    settings,
    buildMode,
    logoUrl,
    duckdb = null,
    conditions = []
}) {
    // The root composer — the report's option titles and its `Translation` block are the app's keys
    const { t } = useI18n();

    // `$launchUrl` is provided by main.js; the fallback is for a mount without it (dev/tests)
    const launchUrl = inject('$launchUrl', (url) => {
        console.warn('$launchUrl not injected - falling back to window.open:', url);
        window.open(url, '_blank');  // Safe fallback for dev/testing
    });

    const { fetchPage, reloadKey, reportWhereClause } = useIssuesGridData({ conn, conditions });

    const { trackEvent } = useAnalytics();
    const {
        generating,
        groupByOptions,
        orderByOptions,
        generateReport
    } = useIssuesGridReport({
        conn,
        settings,
        logoUrl,
        whereClause: reportWhereClause,
        t,
        trackEvent,
        // the plan branch (opt-in `includePlans`, off) fetches issue x/y through the DuckDB service
        duckdb
    });

    // Every deep link of the grid (the issue, its drawing, its location)
    const openUrl = (url) => {
        console.log('Attempting to open URL:', url);  // Debug log to verify

        if (unref(buildMode) === 'production') {
            launchUrl(url);  // Use injected launchUrl
        } else {
            window.open(url, '_blank');
        }
    };

    return computed(() => {
        const cfg = unref(settings) || {};
        const apiUrl = cfg.apiUrl || '';
        return {
            filterContext: unref(filterContext),
            dataLoaded: !!unref(dataLoaded),
            fetchPage,
            reloadKey: unref(reloadKey),
            // the web app's host: the API host without its 'api-' prefix
            apiHost: apiUrl.startsWith('api-') ? apiUrl.slice(4) : apiUrl,
            projectCode: cfg.projectCode || '',
            openUrl,
            generateReport,
            reportBusy: unref(generating),
            reportDisabledModes: ISSUES_REPORT_DISABLED_MODES,
            reportGroupByOptions: groupByOptions,
            reportOrderByOptions: orderByOptions
        };
    });
}
